import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import useOrgId from './useOrgId.js';
import { fontName, fontFormat } from '../helpers/fonts.js';
import isEmpty from 'lodash/isEmpty.js';

const WEIGHTS_LIST = {
  thin: 100,
  extraLight: 200,
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
};
const WEIGHTS_LIST_IN_SEARCH_ORDER = [
  'thin',
  'extralight',
  'light',
  'regular',
  'medium',
  'semibold',
  'extrabold',
  'bold',
  'black',
];

const getFontWeightByFont = ({ namedVariations, subfamilyName }) => {
  if (isEmpty(namedVariations)) {
    const subfamilyKey = WEIGHTS_LIST_IN_SEARCH_ORDER.find(weightKey => subfamilyName?.toLowerCase().includes(weightKey));
    /**
     * A static font should avoid allowing faux/fake weight to be generated by the browser. (unexpected and ugly result)
     * By setting full range of weights as default we minimize this risk. Rather use separate font for different weights
     * However as we still need to support setting primary/secondary fonts which then can be styled we still need to
     * try to figure out a weight
     */
    return WEIGHTS_LIST[subfamilyKey] || '100 1000';
  }
  // For a variable font we set the range to the weights provided by the font designer
  return Object.values(namedVariations).reduce((accumulatedWeights, { wght }) => {
    if (!wght) return accumulatedWeights;
    if (!accumulatedWeights.length) {
      accumulatedWeights.push(wght);
    } else if (wght <= accumulatedWeights[0]) {
      accumulatedWeights[0] = wght;
    } else if (!accumulatedWeights[1]) {
      accumulatedWeights[1] = wght;
    } else if (wght > accumulatedWeights[1]) {
      accumulatedWeights[1] = wght;
    }
    return accumulatedWeights;
  }, []).join(' ');
};

const useAssets = () => {
  const orgId = useOrgId();
  const firebase = useFirebase();
  const queryClient = useQueryClient();

  const parseFont = async font => {
    const agent = await firebase.getAgent();
    const { url } = font;
    font.format = fontFormat(font.filename);
    try {
      const { body } = await agent.post('/themes/font-parser').send({ url });
      font.originalFamilyName = body?.familyName;
      body.familyName = fontName(body?.familyName, font.name);
      const version = body.version.split(';')[0];
      if (!!version) body.familyName += ` ${version}`;
      body.familyName = body.familyName.replace(/[^a-zA-Z0-9]/g, ''); // Please remove this when Player v1 makes sure to wrap "" around custom fonts or when it is deprecated
      font.style = body.italicAngle === 0 ? 'normal' : 'italic';
      font.weight = getFontWeightByFont(body);
      return { ...font, ...body };
    } catch (error) {
      console.error('Failed to parse the font', error.message);
      return { ...font, error };
    }
  };

  const fontAssets = useQuery({
    queryKey: ['fonts', orgId],
    queryFn: async () => {
      const agent = await firebase.getAgent();
      const response = await agent.get(`/o/${orgId}/assets?type=font`);
      if (!response?.body?.assets) return [];
      const { assets } = response.body;
      return await Promise.all(assets.map(async font => parseFont(font)));
    },
  });

  const addFontAsset = useMutation({
    mutationFn: async font => {
      const agent = await firebase.getAgent();
      const response = await agent.post(`/o/${encodeURIComponent(orgId)}/assets/${font.name}`).attach('file', font);
      if (response?.body) {
        const { assetType: type, name, storageFileName: filename, storageUrl: url } = Object.values(response.body)[0];
        const id = Object.keys(response.body)[0];
        const parsedFont = await parseFont({
          id,
          type,
          name,
          filename,
          url,
        });
        queryClient.setQueryData(['fonts', orgId], (currentFonts = []) => [...currentFonts, parsedFont]);
      }
      return response?.body;
    },
  });

  const removeFontAsset = useMutation({
    mutationFn: async font => {
      const agent = await firebase.getAgent();
      const response = await agent.delete(`/o/${encodeURIComponent(orgId)}/assets/fonts/${encodeURIComponent(font.filename)}`);
      if (response?.status === 204) {
        queryClient.setQueryData(['fonts', orgId], currentFonts => currentFonts.filter(currentFont => currentFont.id !== font.id));
      }
      return response?.body;
    },
  });

  return { removeFontAsset, fontAssets, addFontAsset };
};

export default useAssets;
